import * as React from 'react';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  Chip,
  Checkbox,
  ListItemText,
  Typography,
  Paper,
  IconButton,
  Collapse,
  Divider,
  Tooltip,
} from '@mui/material';

import { GeoConditionInput } from './GeoConditionInput';
import { useConditionBuilder } from '../../../hooks/useConditionBuilder';
import { useFormFields } from '../../../hooks/useFormFields';
import { useOptimizedHydration } from '../../../hooks/useOptimizedHydration';
import { useRuleStore } from '../../../state/ruleStore';
import {
  generateConditionPreview,
  generateConditionPreviewDetailed,
  getGroupingExplanation,
  validateCanAddCondition,
  MAX_CONDITIONS,
} from '../../../utils/conditionPreview';
import { mapOperatorsFromBackend } from '../../../utils/operatorMapping';
import InternalButton from 'components/InternalButton';
import { useThemeContext } from 'global/ThemeContext/ThemeContext';

import type { Plugin } from '../../../hooks/useAutomationDefinitions';
import type {
  LogicalOp,
  ConditionGroup,
  ConditionLeaf,
  ConditionNode,
  Operator,
} from '../../../types/rule';

type Props = { plugin: Plugin };

/**
 * Componente específico para condições do evento "form_submission"
 * Lida com seleção de campos de formulários e suas opções
 */
export const FormSubmissionConditions: React.FC<Props> = () => {
  const theme = useThemeContext();

  // Hooks SEMPRE no topo
  const { draft, setConditionsRoot, selectedTrigger } = useRuleStore();
  const {
    ensureRoot,
    addRule,
    setGroupLogical,
    deleteChildAtPath: deleteChildAtPathHook,
    addRuleToGroup,
    addGroupToGroup,
  } = useConditionBuilder();
  const { fields, loading: fieldsLoading, getOptions, optionsCache } = useFormFields();

  const { hydrateAllConditionsBatch } = useOptimizedHydration();

  // garante root
  React.useEffect(() => {
    ensureRoot();
  }, [ensureRoot]);

  // Estado para controlar se a hidratação já foi feita
  const [hydrationCompleted, setHydrationCompleted] = React.useState(false);

  // Estado para controlar grupos expandidos
  const [expandedGroups, setExpandedGroups] = React.useState<Record<string, boolean>>({});

  // Função para atualizar uma folha em um caminho específico
  const updateLeafAtPath = React.useCallback(
    (path: number[], patch: Partial<ConditionLeaf>) => {
      if (!draft.conditionsRoot) return;

      console.log(`🔧 updateLeafAtPath chamado:`, {
        path,
        patch,
        nodeId: patch.id || 'unknown',
        currentRoot: draft.conditionsRoot,
      });

      // Usar o mesmo método que funciona no useConditionBuilder
      const root = JSON.parse(JSON.stringify(draft.conditionsRoot)) as ConditionGroup;

      if (path.length === 0) return;

      if (path.length === 1) {
        const child = root.children[path[0]] as ConditionLeaf;
        const updatedChild = { ...child, ...patch };
        root.children[path[0]] = updatedChild;

        console.log(` Atualizando folha no nível raiz:`, {
          path,
          oldChild: child,
          newChild: updatedChild,
          updatedRoot: root,
        });

        // Forçar atualização do Zustand com o novo objeto
        setConditionsRoot(root);
        return;
      }

      let current = root;
      for (let i = 0; i < path.length - 1; i++) {
        current = current.children[path[i]] as ConditionGroup;
      }
      const leaf = current.children[path[path.length - 1]] as ConditionLeaf;
      const updatedLeaf = { ...leaf, ...patch };
      current.children[path[path.length - 1]] = updatedLeaf;

      console.log(` Atualizando folha aninhada:`, {
        path,
        oldLeaf: leaf,
        newLeaf: updatedLeaf,
        updatedRoot: root,
      });

      // Forçar atualização do Zustand com o novo objeto
      setConditionsRoot(root);
    },
    [draft.conditionsRoot, setConditionsRoot]
  );

  // Hidratar dados das condições quando forem carregadas
  React.useEffect(() => {
    const hydrateConditions = async () => {
      if (!draft.conditionsRoot || !fields.length || hydrationCompleted) return;

      console.log('🔄 === INÍCIO HIDRATAÇÃO BATCH ===');
      console.log('🌳 Árvore atual:', draft.conditionsRoot);
      console.log('📋 Campos disponíveis:', fields.length);
      console.log('🏁 Hidratação já completada?', hydrationCompleted);

      // Marcar como em processo para evitar múltiplas execuções
      setHydrationCompleted(true);

      // 1. Coletar todas as condições que precisam ser hidratadas
      const conditionsToHydrate: Array<{ rightOperand: string; nodeId: string }> = [];

      const collectConditions = (node: ConditionNode): void => {
        if (node.type === 'rule') {
          const leaf = node as ConditionLeaf;
          console.log(`🔍 Analisando folha ${leaf.id}:`, {
            left: leaf.left,
            right: leaf.right,
            rightType: typeof leaf.right,
            meta: leaf.meta,
            hasOptions: leaf.meta?.options && leaf.meta.options.length > 0,
          });

          //  OTIMIZAÇÃO: Só hidratar se NÃO tiver options em meta
          const alreadyHydrated = leaf.meta?.options && leaf.meta.options.length > 0;

          if (
            leaf.left === 'submission.option_id' &&
            leaf.right &&
            typeof leaf.right === 'string' &&
            !alreadyHydrated //  Pular se já tem options do resource_data!
          ) {
            console.log(` Folha ${leaf.id} precisa de hidratação com right_operand: ${leaf.right}`);
            conditionsToHydrate.push({
              rightOperand: leaf.right,
              nodeId: leaf.id,
            });
          } else {
            if (alreadyHydrated) {
              console.log(`⚡ Folha ${leaf.id} JÁ HIDRATADA com resource_data - pulando!`);
            } else {
              console.log(`❌ Folha ${leaf.id} NÃO precisa de hidratação`);
            }
          }
        } else if (node.type === 'group') {
          const group = node as ConditionGroup;
          console.log(`📁 Analisando grupo ${group.id} com ${group.children.length} filhos`);
          for (const child of group.children) {
            collectConditions(child);
          }
        }
      };

      // Coletar condições da árvore
      for (const child of draft.conditionsRoot.children) {
        collectConditions(child);
      }

      console.log(`📋 Encontradas ${conditionsToHydrate.length} condições para hidratar`);

      if (conditionsToHydrate.length === 0) {
        console.log('⚡ Nenhuma condição precisa de hidratação - resource_data otimizou tudo!');
        setHydrationCompleted(true);
        return;
      }

      console.log('🔄 Hidratando condições que não têm resource_data...');

      // 2. Hidratar todas as condições de uma vez
      const hydrationResults = await hydrateAllConditionsBatch(conditionsToHydrate);

      // 3. Aplicar TODOS os resultados de uma vez para evitar sobrescrita
      console.log(
        `🔄 Aplicando resultados de hidratação para ${conditionsToHydrate.length} condições...`
      );

      // Criar uma cópia do estado atual
      const root = JSON.parse(JSON.stringify(draft.conditionsRoot)) as ConditionGroup;
      let hasUpdates = false;

      for (let i = 0; i < conditionsToHydrate.length; i++) {
        const condition = conditionsToHydrate[i];
        console.log(
          `🔍 Processando condição ${condition.nodeId} com right_operand: ${condition.rightOperand}`
        );
        const result = hydrationResults[condition.nodeId];

        if (result?.field) {
          console.log(` Resultado encontrado para ${condition.nodeId}:`, {
            fieldId: result.field.id,
            fieldLabel: result.field.label,
            optionsCount: result.options.length,
            options: result.options.map(opt => ({ id: opt.id, label: opt.option_label })),
          });

          const path = findNodePath(root, condition.nodeId);
          console.log(`📍 Caminho encontrado para ${condition.nodeId}:`, path);

          if (path.length > 0) {
            // Aplicar field_uuid e options diretamente no root
            if (path.length === 1) {
              const child = root.children[path[0]] as ConditionLeaf;
              child.meta = {
                field_uuid: result.field.id,
                field_label: result.field.label,
                options: result.options,
              };
            } else {
              let current = root;
              for (let j = 0; j < path.length - 1; j++) {
                current = current.children[path[j]] as ConditionGroup;
              }
              const leaf = current.children[path[path.length - 1]] as ConditionLeaf;
              leaf.meta = {
                field_uuid: result.field.id,
                field_label: result.field.label,
                options: result.options,
              };
            }

            hasUpdates = true;
            console.log(
              `💾 Aplicado resultado para condição ${condition.nodeId}: ${result.field.label} (${result.options.length} opções)`
            );
          } else {
            console.log(`❌ Caminho não encontrado para condição ${condition.nodeId}`);
          }
        } else {
          console.log(`❌ Nenhum resultado encontrado para condição ${condition.nodeId}`);
        }
      }

      // Aplicar todas as atualizações de uma vez só
      if (hasUpdates) {
        console.log(`🚀 Aplicando todas as atualizações de uma vez só...`);
        setConditionsRoot(root);
      }

      console.log(' === FIM HIDRATAÇÃO BATCH ===');
    };

    hydrateConditions();
  }, [
    draft.conditionsRoot,
    fields,
    hydrationCompleted,
    hydrateAllConditionsBatch,
    updateLeafAtPath,
  ]);

  // Reset da hidratação quando as condições mudarem (nova regra carregada)
  React.useEffect(() => {
    setHydrationCompleted(false);
  }, [draft.uuid]); // Reset quando o UUID da regra mudar

  // Função auxiliar para encontrar o caminho de um nó na árvore
  const findNodePath = (root: ConditionGroup, nodeId: string): number[] => {
    const findPath = (
      current: ConditionNode,
      targetId: string,
      currentPath: number[]
    ): number[] | null => {
      if (current.id === targetId) {
        return currentPath;
      }

      if (current.type === 'group') {
        const group = current as ConditionGroup;
        for (let i = 0; i < group.children.length; i++) {
          const childPath = findPath(group.children[i], targetId, [...currentPath, i]);
          if (childPath) return childPath;
        }
      }

      return null;
    };

    for (let i = 0; i < root.children.length; i++) {
      const path = findPath(root.children[i], nodeId, [i]);
      if (path) return path;
    }

    return [];
  };

  const hasRoot = Boolean(draft.conditionsRoot);

  // ✅ NOVO: Detectar automaticamente left_operand baseado no field_type
  const getLeftOperandFromFieldType = (fieldType: string): string => {
    // field_types que usam submission.option_id (selecionáveis)
    const optionIdTypes = ['dropdown', 'checkbox', 'radio'];

    // field_types que usam submission.value (texto/geo)
    const valueTypes = ['tags', 'textarea', 'text', 'geo_state'];

    if (optionIdTypes.includes(fieldType)) {
      return 'submission.option_id';
    }

    if (valueTypes.includes(fieldType)) {
      return 'submission.value';
    }

    // Fallback
    console.warn('⚠️ field_type desconhecido:', fieldType);
    return 'submission.option_id';
  };

  // helpers
  const operatorsForLeft = (left: string): Operator[] => {
    //  Usar condition_definitions do trigger selecionado
    if (selectedTrigger?.condition_definitions) {
      const condDef = selectedTrigger.condition_definitions.find((cd: any) => cd.key === left);
      if (condDef && condDef.supported_operators) {
        // Converter operadores do backend para formato interno
        return condDef.supported_operators.map((op: string) =>
          op.replace(/'/g, '').toUpperCase()
        ) as Operator[];
      }
    }

    // Fallback
    return ['EQUALS', 'NOT_EQUALS'] as Operator[];
  };

  // Obter operadores com labels em português
  const getOperatorOptions = (left: string): Array<{ value: string; label: string }> => {
    console.log('🔍 getOperatorOptions chamado para left:', left);
    console.log('🔍 selectedTrigger:', selectedTrigger);
    console.log('🔍 condition_definitions:', selectedTrigger?.condition_definitions);

    if (selectedTrigger?.condition_definitions) {
      const condDef = selectedTrigger.condition_definitions.find((cd: any) => cd.key === left);
      console.log('🔍 condDef encontrada:', condDef);

      if (condDef && condDef.supported_operators) {
        const mapped = mapOperatorsFromBackend(condDef.supported_operators);
        console.log(' Operadores mapeados:', mapped);
        return mapped;
      }
    }

    // Fallback
    console.log('⚠️ Usando fallback para operadores');
    const ops = operatorsForLeft(left);
    return ops.map(op => ({ value: op, label: op }));
  };

  const isMultiOperator = (op: Operator) => op === 'IN' || op === 'NOT_IN';

  const onChangeLeafOperator = (path: number[], operator: Operator) => {
    const leaf = getNodeAtPath(path) as ConditionLeaf;
    const becomeMulti = isMultiOperator(operator);
    const wasMulti = Array.isArray(leaf.right);
    updateLeafAtPath(path, {
      operator,
      right: becomeMulti
        ? wasMulti
          ? leaf.right
          : []
        : Array.isArray(leaf.right)
          ? ''
          : (leaf.right ?? ''),
    });
  };

  const onChangeLeafRight = (path: number[], value: string | string[]) => {
    updateLeafAtPath(path, { right: value as string | string[] });
  };

  const onChangeLeafField = async (path: number[], fieldId?: string) => {
    const leaf = getNodeAtPath(path) as ConditionLeaf;

    console.log('🔄 Mudando field - Limpando options antigas');
    console.log('🔍 Field antigo:', leaf.meta?.field_uuid);
    console.log('🔍 Field novo:', fieldId);

    if (!fieldId) {
      updateLeafAtPath(path, { meta: {}, right: '' });
      return;
    }

    // Buscar dados do campo selecionado
    const selectedField = fields.find(f => f.id === fieldId);
    console.log('🔍 Campo selecionado:', selectedField);

    if (!selectedField) {
      console.error('❌ Campo não encontrado!');
      return;
    }

    // ✅ NOVO: Detectar automaticamente left_operand baseado no field_type
    const autoLeft = getLeftOperandFromFieldType(selectedField.field_type);
    console.log(
      `✅ left_operand detectado automaticamente: ${autoLeft} (field_type: ${selectedField.field_type})`
    );

    // Obter operadores para o novo left
    const ops = operatorsForLeft(autoLeft);
    const defaultOperator = ops[0] || 'EQUALS';

    //  Criar meta com dados do campo
    const meta: ConditionLeaf['meta'] = {
      field_uuid: fieldId,
      field_label: selectedField.label,
      field_type: selectedField.field_type, //  Salvar field_type!
      form_uuid: selectedField.form?.id,
    };

    console.log('✅ Meta criado:', meta);

    // ✅ Atualizar left, operator E meta de uma vez
    updateLeafAtPath(path, {
      left: autoLeft,
      operator: defaultOperator as Operator,
      meta,
      right: '',
    });

    // Carregar options se necessário (submission.option_id)
    if (autoLeft === 'submission.option_id' && fieldId) {
      console.log('📥 Carregando options para field:', fieldId);
      await getOptions(fieldId);

      // Atualizar meta com options carregadas
      const loadedOptions = optionsCache[fieldId] || [];
      if (loadedOptions.length > 0) {
        updateLeafAtPath(path, {
          meta: { ...meta, options: loadedOptions },
        });
      }
    }
  };

  const onChangeGroupLogical = (path: number[], logical: 'AND' | 'OR') => {
    updateGroupAtPath(path, { logical });
  };

  const getNodeAtPath = (path: number[]): ConditionNode => {
    let current: ConditionNode = draft.conditionsRoot!;
    for (const index of path) {
      current = (current as ConditionGroup).children[index];
    }
    return current;
  };

  // Funções para manipular nós em caminhos específicos

  const updateGroupAtPath = (path: number[], patch: Partial<ConditionGroup>) => {
    if (path.length === 0) {
      // Atualizar root
      setGroupLogical(patch.logical || draft.conditionsRoot!.logical);
      return;
    }
    // Para grupos aninhados, implementar recursão
    const parentGroup = getNodeAtPath(path.slice(0, -1)) as ConditionGroup;
    const group = parentGroup.children[path[path.length - 1]] as ConditionGroup;
    const updatedGroup = { ...group, ...patch };
    const updatedParentGroup = {
      ...parentGroup,
      children: [
        ...parentGroup.children.slice(0, path[path.length - 1]),
        updatedGroup,
        ...parentGroup.children.slice(path[path.length - 1] + 1),
      ],
    };
    updateGroupAtPath(path.slice(0, -1), updatedParentGroup);
  };

  // Usar a função do hook diretamente
  const deleteChildAtPath = deleteChildAtPathHook;

  const addRuleToGroupAtPath = (path: number[], init?: Partial<ConditionLeaf>) => {
    addRuleToGroup(path, init);
  };

  const addGroupToGroupAtPath = (path: number[], logical: 'AND' | 'OR') => {
    addGroupToGroup(path, logical);
  };

  if (!hasRoot) {
    return (
      <Box p={2}>
        <Typography variant="body2" color="text.secondary">
          Inicializando condições…
        </Typography>
      </Box>
    );
  }

  const group = draft.conditionsRoot as ConditionGroup;

  // Componente para renderizar uma condição individual
  const renderCondition = (node: ConditionNode, path: number[], depth: number = 0) => {
    console.log(
      `🎨 Renderizando ${node.type} ${node.id} no path [${path.join(',')}] depth ${depth}`
    );
    if (node.type === 'group') {
      return renderGroup(node as ConditionGroup, path, depth);
    }
    return renderLeaf(node as ConditionLeaf, path, depth);
  };

  // Componente para renderizar um grupo
  const renderGroup = (group: ConditionGroup, path: number[], depth: number) => {
    const expanded = expandedGroups[group.id] ?? true;
    console.log(`📁 Grupo ${group.id} expandido: ${expanded} (${group.children.length} filhos)`);
    const setExpanded = (value: boolean) => {
      setExpandedGroups(prev => ({ ...prev, [group.id]: value }));
    };
    const indent = depth * 20;

    return (
      <Box
        key={group.id}
        //elevation={2}
        sx={{
          //ml: `${indent}px`,
          //border: `2px solid ${theme.primary}20`,
          backgroundColor: `${theme.background.primary}`,
          borderRadius: 2,
          paddingLeft: 1,
          paddingRight: 2,
          paddingY: 1,
        }}
      >
        <Box p={1}>
          {/* Cabeçalho do grupo */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton
                size="small"
                onClick={() => setExpanded(!expanded)}
                sx={{ color: theme.text.primary }}
              >
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
              <Typography variant="subtitle1" sx={{ color: theme.text.primary, fontWeight: 600 }}>
                Grupo de Condições ({group.logical === 'AND' ? 'E' : 'OU'})
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Operador</InputLabel>
                <Select
                  value={group.logical}
                  label="Operador"
                  onChange={e => onChangeGroupLogical(path, e.target.value as 'AND' | 'OR')}
                  disabled
                >
                  <MenuItem value="AND">E</MenuItem>
                  <MenuItem value="OR">OU</MenuItem>
                </Select>
              </FormControl>
              <IconButton
                size="small"
                onClick={() => deleteChildAtPath(path)}
                sx={{ color: theme.error }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Conteúdo do grupo */}
          <Collapse
            in={expanded}
            sx={{
              //border: `2px solid ${theme.primary}`,
              borderRadius: 2,
              padding: 1,
              minHeight: '200px',
              //backgroundColor: 'rgba(255, 0, 0, 0.1)',
              '& .MuiCollapse-wrapper': {
                minHeight: '200px',
              },
              '& .MuiCollapse-wrapperInner': {
                minHeight: '200px',
              },
            }}
          >
            <Box
              sx={{
                border: `1px solid ${theme.text.secondary}`,
                padding: 1,
                minHeight: '150px',
                borderRadius: 2,
              }}
            >
              {/* Debug */}
              {/* <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                DEBUG: Collapse expandido = {expanded.toString()} | Filhos: {group.children.length}{' '}
                | Grupo UUID: {group.id}
              </Typography> */}
              {group.children.map((child, index) => {
                console.log(
                  `📦 Renderizando filho ${child.type} ${child.id} (Backend UUID) dentro do Collapse do grupo ${group.id}`
                );
                return (
                  <Box
                    key={child.id}
                    mb={2}
                    sx={{
                      //border: '1px solid green',
                      borderRadius: 2,
                      padding: 1,
                      minHeight: '80px',
                      //backgroundColor: 'rgba(0, 255, 0, 0.1)',
                    }}
                  >
                    {/* Debug */}
                    {/* <Typography variant="caption" color="primary" sx={{ display: 'block', mb: 1 }}>
                      DEBUG: Filho {index + 1}/{group.children.length} - {child.type} | UUID:{' '}
                      {child.id}
                    </Typography> */}
                    {renderCondition(child, [...path, index], depth + 1)}
                  </Box>
                );
              })}

              {/* Botões de ação para o grupo */}
              {/*  REMOVIDO: Botões de grupo - interface agora é flat apenas */}
              {/* Mantendo apenas "Adicionar condição" no root principal */}
            </Box>
          </Collapse>
        </Box>
      </Box>
    );
  };

  // Componente para renderizar uma folha (condição)
  const renderLeaf = (leaf: ConditionLeaf, path: number[], depth: number) => {
    const ops = operatorsForLeft(leaf.left);
    const fieldId = leaf.meta?.field_uuid ?? '';
    // Usar opções do meta primeiro, depois do cache
    const fieldOptions = leaf.meta?.options ?? (fieldId ? (optionsCache[fieldId] ?? []) : []);
    const indent = depth * 20;

    // Debug: Log dos dados da folha
    const debugData = {
      fieldId,
      fieldLabel: leaf.meta?.field_label,
      rightOperand: leaf.right,
      rightType: typeof leaf.right,
      fieldOptionsCount: fieldOptions.length,
      hasMetaOptions: !!leaf.meta?.options,
      metaOptionsCount: leaf.meta?.options?.length ?? 0,
      selectValue:
        fieldOptions.length > 0 && leaf.right && !Array.isArray(leaf.right)
          ? (leaf.right as string)
          : '',
    };

    console.log(`🍃 Renderizando folha ${leaf.id}:`, debugData);

    const isFirstCondition = path.length === 1 && path[0] === 0;

    return (
      <>
        {/*  Seletor de Operador Lógico (E/OU) - aparece ANTES de cada condição (exceto a primeira) */}
        {!isFirstCondition && (
          <Box display="flex" alignItems="center" my={2}>
            <Divider sx={{ flex: 1, borderColor: theme.text.secondary }} />
            <Tooltip
              title="Define como esta condição será agrupada com as anteriores. As condições são agrupadas automaticamente 2 a 2."
              placement="top"
              arrow
            >
              <FormControl size="small" sx={{ mx: 2, minWidth: 120 }}>
                <InputLabel sx={{ fontSize: '0.75rem', color: theme.text.secondary }}>
                  Concatenação
                </InputLabel>
                <Select
                  value={leaf.logicalOperator || 'AND'}
                  onChange={e => {
                    const updatedLeaf = { ...leaf, logicalOperator: e.target.value as LogicalOp };
                    updateLeafAtPath(path, updatedLeaf);
                  }}
                  label="Concatenação"
                  sx={{
                    '& .MuiSelect-select': {
                      color: theme.text.primary,
                      fontSize: '0.875rem',
                      py: 0.5,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.text.secondary,
                    },
                  }}
                >
                  <MenuItem value="AND">E</MenuItem>
                  <MenuItem value="OR">OU</MenuItem>
                </Select>
              </FormControl>
            </Tooltip>
            <Divider sx={{ flex: 1, borderColor: theme.text.secondary }} />
          </Box>
        )}

        <Paper
          key={leaf.id}
          elevation={0}
          sx={{
            //ml: `${indent}px`,
            border: `1px solid ${theme.text.primary}20`,
            borderRadius: 2,
            backgroundColor: theme.background.primary,
            p: 0,
          }}
        >
          <Box p={2}>
            {/* Cabeçalho da condição */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="body2" color="text.secondary">
                Condição {path[path.length - 1] + 1}
              </Typography>
              <IconButton
                size="small"
                onClick={() => deleteChildAtPath(path)}
                sx={{ color: theme.error }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Debug: Dados da folha */}
            {/* <Box
            sx={{
              backgroundColor: `${theme.background.primary}`,
              padding: 1,
              borderRadius: 1,
              mb: 2,
              fontSize: '0.75rem',
              fontFamily: 'monospace',
            }}
          >
            <Typography
              variant="caption"
              color="primary"
              sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}
            >
              DEBUG: Dados da folha {leaf.id}
            </Typography>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {JSON.stringify(debugData, null, 2)}
            </pre>
          </Box> */}

            <Grid container spacing={2}>
              {/* ✅ NOVO: Primeiro selecionar o campo do formulário */}
              <Grid item xs={12}>
                <Autocomplete
                  loading={fieldsLoading}
                  options={fields}
                  value={fields.find(f => f.id === fieldId) ?? null}
                  onChange={(_, v) => onChangeLeafField(path, v?.id)}
                  getOptionLabel={o => o?.label ?? ''}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box>
                        <Typography variant="body2">{option.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.form?.form_name} • {option.field_type}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={params => (
                    <TextField {...params} size="small" label="Campo do formulário" required />
                  )}
                />
              </Grid>

              {/* ✅ Operador - só aparece após selecionar campo */}
              {fieldId && (
                <Grid item xs={12}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Operador</InputLabel>
                    <Select
                      label="Operador"
                      value={leaf.operator}
                      onChange={e => onChangeLeafOperator(path, e.target.value as Operator)}
                    >
                      {getOperatorOptions(leaf.left).map(op => (
                        <MenuItem key={op.value} value={op.value}>
                          {op.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* ✅ Valor / Opção - só aparece após selecionar campo */}
              {fieldId && (
                <Grid item xs={12}>
                  {leaf.left === 'submission.option_id' ? (
                    //  OPÇÃO SELECIONADA
                    <>
                      {isMultiOperator(leaf.operator) ? (
                        // Múltiplas opções (IN / NOT_IN)
                        <FormControl size="small" fullWidth>
                          <InputLabel>Opções</InputLabel>
                          <Select
                            multiple
                            label="Opções"
                            value={(Array.isArray(leaf.right) ? leaf.right : []) as string[]}
                            onChange={e => onChangeLeafRight(path, e.target.value as string[])}
                            renderValue={selected => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {(selected as string[]).map(v => {
                                  const o = fieldOptions.find(x => x.id === v);
                                  return <Chip key={v} size="small" label={o?.option_label ?? v} />;
                                })}
                              </Box>
                            )}
                          >
                            {fieldOptions.map(o => (
                              <MenuItem key={o.id} value={o.id}>
                                <Checkbox
                                  checked={
                                    Array.isArray(leaf.right) &&
                                    (leaf.right as string[]).indexOf(o.id) > -1
                                  }
                                />
                                <ListItemText primary={o.option_label} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        // Opção única (EQUALS / NOT_EQUALS)
                        <>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Opção</InputLabel>
                            <Select
                              label="Opção"
                              value={
                                fieldOptions.length > 0 && leaf.right && !Array.isArray(leaf.right)
                                  ? (leaf.right as string)
                                  : ''
                              }
                              onChange={e => onChangeLeafRight(path, e.target.value)}
                              disabled={fieldOptions.length === 0}
                            >
                              {fieldOptions.map(o => (
                                <MenuItem key={o.id} value={o.id}>
                                  {o.option_label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          {/*  EXCEÇÃO: Se selecionou "Outro", mostrar campo de texto */}
                          {(() => {
                            const selectedOption = fieldOptions.find(o => o.id === leaf.right);
                            const isOutro = selectedOption?.option_label
                              ?.toLowerCase()
                              .includes('outro');

                            return isOutro ? (
                              <TextField
                                size="small"
                                label="Especifique"
                                placeholder="Digite o valor"
                                fullWidth
                                sx={{ mt: 2 }}
                                value={leaf.meta?.outro_value || ''}
                                onChange={e => {
                                  const meta = {
                                    ...(leaf.meta ?? {}),
                                    outro_value: e.target.value,
                                  };
                                  updateLeafAtPath(path, { meta });
                                }}
                              />
                            ) : null;
                          })()}
                        </>
                      )}
                    </>
                  ) : (
                    //  VALOR DIGITADO PELO USUÁRIO
                    <>
                      {/* Verificar tipo do campo (prioridade: meta.field_type > buscar em fields) */}
                      {(() => {
                        //  OTIMIZAÇÃO: Usar field_type do meta (resource_data)
                        let fieldType = leaf.meta?.field_type;

                        // Fallback: buscar em fields se não tiver no meta
                        if (!fieldType && leaf.meta?.field_uuid) {
                          const selectedField = fields.find(f => f.id === leaf.meta?.field_uuid);
                          fieldType = selectedField?.field_type;
                        }

                        console.log('🔍 Renderizando input para field_type:', fieldType);

                        //  Campo GEO: Estado + Cidade
                        if (fieldType === 'geo_state') {
                          return (
                            <GeoConditionInput
                              value={
                                Array.isArray(leaf.right) ? '' : ((leaf.right as string) ?? '')
                              }
                              onChange={(value, extras) => {
                                // ✅ Salvar value (para exibição) E extras (state/city para backend)
                                onChangeLeafRight(path, value);

                                if (extras) {
                                  const updatedMeta = {
                                    ...(leaf.meta ?? {}),
                                    geo_state: extras.state,
                                    geo_city: extras.city,
                                  };
                                  updateLeafAtPath(path, { meta: updatedMeta });
                                  console.log('🌍 Meta atualizado com geo:', updatedMeta);
                                }
                              }}
                            />
                          );
                        }

                        //  Campo TEXTO normal (text, textarea, tags, etc)
                        return (
                          <TextField
                            size="small"
                            label="Valor"
                            placeholder="Digite o valor"
                            fullWidth
                            multiline={fieldType === 'textarea'} //  Multiline para textarea
                            rows={fieldType === 'textarea' ? 3 : 1}
                            value={Array.isArray(leaf.right) ? '' : ((leaf.right as string) ?? '')}
                            onChange={e => {
                              const textValue = e.target.value;
                              console.log('📝 TextField onChange:', textValue);

                              // ✅ Atualizar right E meta.option_value de uma vez
                              const updatedMeta = {
                                ...(leaf.meta ?? {}),
                                option_value: textValue,
                              };

                              updateLeafAtPath(path, {
                                right: textValue,
                                meta: updatedMeta,
                              });

                              console.log('✅ Meta atualizado com option_value:', updatedMeta);
                            }}
                          />
                        );
                      })()}
                    </>
                  )}
                </Grid>
              )}
            </Grid>
          </Box>
        </Paper>
      </>
    );
  };

  //  Gerar preview da lógica
  const conditionsList = group.children.filter(c => c.type === 'rule') as ConditionLeaf[];
  const previewText = generateConditionPreview(conditionsList);
  const previewDetailed = generateConditionPreviewDetailed(conditionsList);
  const explanationText = getGroupingExplanation(conditionsList.length);

  return (
    <Box p={2}>
      {/* Título default em relação ao trigger selecionado */}
      <Typography variant="body1" mb={1}>
        Quando <b>{selectedTrigger?.name}</b> {group.children.length >= 1 && 'e'}{' '}
        <b>
          {group.children.length > 1
            ? 'As Condições são Verdadeiras'
            : group.children.length === 1
              ? 'A Condição é Verdadeira'
              : ''}
        </b>{' '}
        então{' '}
      </Typography>

      {/* Preview da Lógica de Agrupamento */}
      {conditionsList.length > 1 && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            backgroundColor: theme.background.primary,
            borderRadius: 2,
            border: `1px dashed ${theme.primary}`,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: theme.text.secondary, display: 'block', mb: 1 }}
          >
            Estrutura da Regra (agrupamento automático 2 a 2):
          </Typography>

          {/* Preview simplificado */}
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              color: theme.primary,
              fontWeight: 600,
              mb: 0.5,
            }}
          >
            {previewText}
          </Typography>

          {/* Preview detalhado (com nomes dos campos) */}
          {conditionsList.some(c => c.meta?.field_label) && (
            <Typography
              variant="caption"
              sx={{
                fontFamily: 'monospace',
                color: theme.text.secondary,
                display: 'block',
                mb: 1,
                fontSize: '0.7rem',
              }}
            >
              {previewDetailed.length > 80
                ? previewDetailed.substring(0, 77) + '...'
                : previewDetailed}
            </Typography>
          )}

          {explanationText && (
            <Typography variant="caption" sx={{ color: theme.text.secondary, fontStyle: 'italic' }}>
              {explanationText}
            </Typography>
          )}
        </Box>
      )}

      {/* Lista de condições */}
      {group.children.length > 0 && (
        <Box
          mb={3}
          sx={{
            //border: `1px solid ${theme.text.secondary}`,
            borderRadius: 2,
            //padding: 2,
            //minHeight: '500px',
            maxHeight: 'none',
            overflow: 'visible',
          }}
        >
          {/* REMOVIDO: Seletor de operador do grupo raiz */}
          {/* Interface agora é flat - operadores estão nas condições individuais */}

          {/* RENDERIZAR APENAS FOLHAS (lista flat) */}
          {conditionsList.map((leaf, index) => {
            console.log(`🌳 Renderizando condição ${index + 1}: ${leaf.meta?.field_label || '?'}`);
            return (
              <Box key={leaf.id} mb={2}>
                {renderLeaf(leaf, [index], 0)}
              </Box>
            );
          })}
        </Box>
      )}
      {/* Ações para o root */}
      <Box display="flex" mt={2} gap={2} flexWrap="wrap" flexDirection="column">
        {/* Validação de limite */}
        {(() => {
          const validation = validateCanAddCondition(conditionsList.length);

          return (
            <>
              <Box display="flex" gap={2} alignItems="center">
                <InternalButton
                  background={theme.primary}
                  variant="primary"
                  icon={<AddIcon />}
                  onClick={() => {
                    if (!validation.canAdd) {
                      alert(validation.error);
                      return;
                    }
                    // ✅ Valores padrão - usuário escolhe campo primeiro, depois detecta automaticamente
                    addRule({
                      left: 'submission.option_id', // Será atualizado ao selecionar campo
                      operator: 'EQUALS',
                      right: '',
                    });
                  }}
                  disabled={!validation.canAdd}
                >
                  Adicionar condição {!validation.canAdd && `(Máx: ${MAX_CONDITIONS})`}
                </InternalButton>

                {/* Contador de condições */}
                <Typography variant="caption" sx={{ color: theme.text.secondary }}>
                  {conditionsList.length} / {MAX_CONDITIONS} condições
                </Typography>
              </Box>

              {/*  Avisos de limite */}
              {validation.warning && (
                <Box
                  sx={{
                    p: 1.5,
                    backgroundColor: `${theme.warning || '#ff9800'}20`,
                    borderRadius: 1,
                    border: `1px solid ${theme.warning || '#ff9800'}`,
                  }}
                >
                  <Typography variant="caption" sx={{ color: theme.warning || '#ff9800' }}>
                    ⚠️ {validation.warning}
                  </Typography>
                </Box>
              )}
            </>
          );
        })()}
      </Box>
    </Box>
  );
};
